import { useRouterState } from '@tanstack/react-router';
import { useCallback, useRef } from 'react';

/**
 * The row the palette sent you to.
 *
 * Searching for a person or a device lands on the page that lists them, and a
 * page listing nine of them has not answered the question by arriving. The id
 * rides in `?focus=`, so the destination is a link somebody can send -- the same
 * rule `/jobs/:id` and `/tasks/:id` already follow, in the one place where the
 * thing being pointed at is a row of a table rather than a route of its own.
 *
 * The ref goes on the matching row. It is a callback rather than an object ref
 * and the scroll happens when the row attaches, because on a cold load the page
 * arrives before its people do: an effect keyed on the id alone would run against
 * an empty table and never run again. One scroll per id, so a refetch that
 * remounts the row does not yank the page back while somebody is reading it.
 *
 * One ref for the table rather than one per row: the rows are drawn in a loop,
 * and a hook per row would change how many hooks the component calls every time
 * the fleet does.
 */
export function useFocusedRow<T extends HTMLElement>(): {
  /** The id the palette asked for, or undefined when nothing was asked for. */
  focus: string | undefined;
  ref: (node: T | null) => void;
} {
  const focus = useRouterState({
    select: (state) => (state.location.search as { focus?: string }).focus,
  });
  const scrolled = useRef<string | null>(null);

  const ref = useCallback(
    (node: T | null) => {
      if (!node || !focus || scrolled.current === focus) return;
      scrolled.current = focus;
      node.scrollIntoView({ block: 'center', behavior: 'smooth' });
    },
    [focus],
  );

  return { focus, ref };
}
